import React, { useState } from "react";
import { Box, Typography, Card, CardContent, Button, Chip, Divider } from "@mui/material";
import { motion } from "framer-motion";
import { primaryColor } from "../../../Theme";
import InteractionsSummaryHeader from "./InteractionsSummaryHeader";
import type { InteractionItem, DrugInteractionCounts } from "./types";
import { getSeverityColor, isHighSeverity, isModerateSeverity, isMinorSeverity } from "./severityUtils";
import { INTENT_HIGH_SEVERITY, INTENT_MODERATE_AND_MINOR_SEVERITY } from "./constants";

export interface InteractionsSummaryCardProps {
  allInteractions: InteractionItem[];
  intent?: string;
  apiCounts?: DrugInteractionCounts;
}

const INITIAL_DISPLAY_COUNT = 3;

const expandCollapseBoxSx = {
  p: 1.25,
  borderTop: "1px solid",
  borderColor: "divider",
  bgcolor: "grey.50",
  display: "flex",
  justifyContent: "center",
} as const;

const expandCollapseButtonSx = {
  fontSize: "0.7rem",
  fontWeight: 600,
  textTransform: "none" as const,
  bgcolor: primaryColor,
  color: "white",
  px: 1.5,
  py: 0.5,
};

const InteractionsSummaryCard: React.FC<InteractionsSummaryCardProps> = ({ allInteractions, intent, apiCounts }) => {
  const [showAll, setShowAll] = useState(false);

  // Normalize intent for case-insensitive comparison
  const intentLower = (intent ?? "").toString().toLowerCase();

  // Filter by intent: high_severity → only high; moderate_and_minor_severity → only moderate+minor
  const interactionsToShow = (() => {
    if (intentLower === INTENT_HIGH_SEVERITY) {
      return allInteractions.filter((item) => isHighSeverity(item.severityLevel));
    }
    if (intentLower === INTENT_MODERATE_AND_MINOR_SEVERITY) {
      return allInteractions.filter(
        (item) => isModerateSeverity(item.severityLevel) || isMinorSeverity(item.severityLevel)
      );
    }
    return allInteractions;
  })();

  const apiTotalCount = apiCounts
    ? (apiCounts.high ?? 0) + (apiCounts.moderate ?? 0) + (apiCounts.minor ?? 0)
    : 0;

  const useApiForHeader = Boolean(apiCounts && (apiCounts.high !== undefined || apiCounts.moderate !== undefined || apiCounts.minor !== undefined));
  const hasApiCountsToDisplay = useApiForHeader;

  if (interactionsToShow.length === 0 && !hasApiCountsToDisplay) {
    return null;
  }

  const displayedInteractions = showAll ? interactionsToShow : interactionsToShow.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMore = interactionsToShow.length > INITIAL_DISPLAY_COUNT;

  const highDrugCount = interactionsToShow.filter(
    (item) => isHighSeverity(item.severityLevel) && item.type === "drug"
  ).length;
  const highAllergyCount = interactionsToShow.filter(
    (item) => isHighSeverity(item.severityLevel) && item.type === "allergy"
  ).length;
  const moderateDrugCount = interactionsToShow.filter(
    (item) => isModerateSeverity(item.severityLevel) && item.type === "drug"
  ).length;
  const moderateAllergyCount = interactionsToShow.filter(
    (item) => isModerateSeverity(item.severityLevel) && item.type === "allergy"
  ).length;
  const minorDrugCount = interactionsToShow.filter(
    (item) => isMinorSeverity(item.severityLevel) && item.type === "drug"
  ).length;
  const minorAllergyCount = interactionsToShow.filter(
    (item) => isMinorSeverity(item.severityLevel) && item.type === "allergy"
  ).length;

  const hasHigh = highDrugCount + highAllergyCount > 0;
  const hasModerate = moderateDrugCount + moderateAllergyCount > 0;
  const hasMinor = minorDrugCount + minorAllergyCount > 0;

  const hasAnyDrug = highDrugCount + moderateDrugCount + minorDrugCount > 0;
  const hasAnyAllergy = highAllergyCount + moderateAllergyCount + minorAllergyCount > 0;
  const headerTypeLabel: "Drug" | "Allergy" | "Drug/Allergy" =
    hasAnyDrug && hasAnyAllergy ? "Drug/Allergy" : hasAnyDrug ? "Drug" : "Allergy";

  const formatSeverityCount = (
    label: string,
    drugCount: number,
    allergyCount: number,
    useSum: boolean
  ): string => {
    if (useSum) return `${label}(${drugCount + allergyCount})`;
    if (allergyCount === 0) return `${label}(${drugCount})`;
    if (drugCount === 0) return `${label}(${allergyCount})`;
    return `${label}(${drugCount}/${allergyCount})`;
  };

  // Title and badges based on which severities are present (or intent for moderate_and_minor_severity / high_severity)
  // When apiCounts is provided, use it for header total and badges; otherwise derive from interactionsToShow
  const isHighSeverityIntent = intentLower === INTENT_HIGH_SEVERITY;
  const highListedCount = highDrugCount + highAllergyCount;
  // API counts can be 0 for drug-drug "high" while allergy rows still appear in high_severity_descriptions
  const highN = useApiForHeader
    ? Math.max(apiCounts!.high ?? 0, highListedCount)
    : highListedCount;
  const modN = useApiForHeader ? (apiCounts!.moderate ?? 0) : moderateDrugCount + moderateAllergyCount;
  const minN = useApiForHeader ? (apiCounts!.minor ?? 0) : minorDrugCount + minorAllergyCount;
  const hasHighFromHeader = highN > 0;
  const hasModerateFromHeader = modN > 0 || (useApiForHeader && apiCounts?.moderate !== undefined);
  const hasMinorFromHeader = minN > 0 || (useApiForHeader && apiCounts?.minor !== undefined);

  const headerTitle = (() => {
    if (isHighSeverityIntent && hasHighFromHeader) return "High Interactions Summary:";
    if (
      intentLower === INTENT_MODERATE_AND_MINOR_SEVERITY &&
      (hasModerateFromHeader || hasMinorFromHeader) &&
      !hasHighFromHeader
    ) {
      return "Moderate and Minor Interactions Summary:";
    }
    if (hasHighFromHeader && !hasModerateFromHeader && !hasMinorFromHeader) return "High Interactions Summary:";
    if (!hasHighFromHeader && hasModerateFromHeader && hasMinorFromHeader) return "Moderate and Minor Interactions Summary:";
    if (!hasHighFromHeader && hasModerateFromHeader && !hasMinorFromHeader) return "Moderate Interactions Summary:";
    if (!hasHighFromHeader && !hasModerateFromHeader && hasMinorFromHeader) return "Minor Interactions Summary:";
    return "High, Moderate and Minor Interactions Summary:";
  })();

  const headerBadges = (() => {
    const badges: Array<{ label: string; color: "error" | "warning" | "info"; countText: string }> = [];
    if (isHighSeverityIntent) {
      if (highN > 0) {
        badges.push({
          label: "High",
          color: "error",
          countText: formatSeverityCount("High", highDrugCount, highAllergyCount, false),
        });
      }
      // First extract / update: show high line items but still surface moderate/minor as counts only.
      if (useApiForHeader && apiCounts) {
        if (modN > 0) badges.push({ label: "Moderate", color: "warning", countText: `Moderate(${modN})` });
        if (minN > 0) badges.push({ label: "Minor", color: "info", countText: `Minor(${minN})` });
      }
      return badges;
    }
    if (useApiForHeader && apiCounts) {
      if (highN > 0) badges.push({ label: "High", color: "error", countText: `High(${highN})` });
      if (apiCounts.moderate !== undefined) badges.push({ label: "Moderate", color: "warning", countText: `Moderate(${apiCounts.moderate ?? 0})` });
      if (apiCounts.minor !== undefined) badges.push({ label: "Minor", color: "info", countText: `Minor(${apiCounts.minor ?? 0})` });
      return badges;
    }
    const isModerateAndMinorIntent = intentLower === INTENT_MODERATE_AND_MINOR_SEVERITY;
    if (hasHigh) {
      badges.push({
        label: "High",
        color: "error",
        countText: formatSeverityCount("High", highDrugCount, highAllergyCount, false),
      });
    }
    if (hasModerate) {
      badges.push({
        label: "Moderate",
        color: "warning",
        countText: formatSeverityCount(
          "Moderate",
          moderateDrugCount,
          moderateAllergyCount,
          isModerateAndMinorIntent
        ),
      });
    }
    if (hasMinor) {
      badges.push({
        label: "Minor",
        color: "info",
        countText: formatSeverityCount("Minor", minorDrugCount, minorAllergyCount, isModerateAndMinorIntent),
      });
    }
    return badges;
  })();

  const headerTotalCount = (() => {
    if (isHighSeverityIntent) return highN > 0 ? highN : interactionsToShow.length;
    if (useApiForHeader && apiCounts)
      return highN + (apiCounts.moderate ?? 0) + (apiCounts.minor ?? 0);
    return interactionsToShow.length;
  })();

  if (headerTotalCount === 0 && interactionsToShow.length === 0 && !hasApiCountsToDisplay) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1.5,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          mb: 1.5,
          bgcolor: "white",
        }}
      >
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          <InteractionsSummaryHeader
            title={headerTitle}
            totalCount={headerTotalCount}
            badges={headerBadges}
            typeLabel={headerTypeLabel}
          />
          {intentLower !== INTENT_MODERATE_AND_MINOR_SEVERITY &&
          displayedInteractions.map((item, index) => {
            const severityColor = getSeverityColor(item.severityLevel);
            return (
              <React.Fragment key={`interaction-${item.id}-${index}`}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "start",
                    py: 0.75,
                    px: 1.25,
                    gap: 0.75,
                    flexWrap: "wrap",
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: "150px",
                      display: "flex",
                      alignItems: "start",
                      gap: 0.75, 
                    }}
                  >
                    <Chip
                      label={item.type === "allergy" ? "Allergy" : "Drug"}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.6rem",
                        height: "18px",
                        borderWidth: 1,
                        borderColor: "info.main",
                        "& .MuiChip-label": { px: 0.5 },
                      }}
                    />
                    {item.description && ( <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        lineHeight: 1.3,
                        fontWeight: 500,
                      }}
                    >
                      {item.description}
                    </Typography>
                    )}
                  </Box>
                  <Chip
                    label={item.severityLevel}
                    color={severityColor}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.6rem",
                      height: "18px",
                      minWidth: "45px",
                      "& .MuiChip-label": { px: 0.5 },
                    }}
                  />
                </Box>
                {index < displayedInteractions.length - 1 && (
                  <Divider sx={{ mx: 1.25, borderColor: "divider" }} />
                )}
              </React.Fragment>
            );
          })}
          {hasMore && !showAll && intentLower !== INTENT_MODERATE_AND_MINOR_SEVERITY && (
            <Box sx={expandCollapseBoxSx}>
              <Button
                variant="contained"
                size="small"
                onClick={() => setShowAll(true)}
                sx={expandCollapseButtonSx}
              >
                View More ({interactionsToShow.length - INITIAL_DISPLAY_COUNT} more)
              </Button>
            </Box>
          )}
          {showAll && hasMore && intentLower !== INTENT_MODERATE_AND_MINOR_SEVERITY && (
            <Box sx={expandCollapseBoxSx}>
              <Button
                variant="contained"
                size="small"
                onClick={() => setShowAll(false)}
                sx={expandCollapseButtonSx}
              >
                Show Less
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default InteractionsSummaryCard;
